import React from 'react';
import {
    Collapse,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ViewList from '@material-ui/icons/ViewList';
import {getResources, useGetResourceLabel} from 'ra-core';
import {Menu, MenuItemLink} from 'react-admin';
import {shallowEqual, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {useUserTotalCount, UserCountBadge} from './UserTotalCount';
import {
    ResourceCountLabel,
    useAdminResourceTotalCount,
} from './AdminResourceTotalCount';

const BOX_RESOURCES = [
    'loyaltyadmin',
    'boxreservation',
    'boxoffer',
    'boxoption',
    'boxdeliveryzone',
    'boxsettings',
    'boxcontractidentity',
    'boxcontractsettings',
];

const BOX_LABELS = {
    loyaltyadmin: 'Prospects',
    boxreservation: 'Réservations',
    boxoffer: 'Offres',
    boxoption: 'Options',
    boxdeliveryzone: 'Zones de livraison',
    boxsettings: 'Paramètres',
    boxcontractidentity: 'Identités contrats',
    boxcontractsettings: 'Contrat',
};

const GROUPS = [
    {
        key: 'catalogue',
        label: 'Catalogue',
        items: [
            {name: 'song', label: 'Karaoké'},
            {name: 'catalog', label: 'Playlist Catalogue'},
            {name: 'song-suggestion', label: 'Suggestion'},
            {name: 'songgenre', label: 'Genre'},
            {name: 'songregion', label: 'Regions'},
            {name: 'songlang', label: 'Langues'},
        ],
    },
    {
        key: 'interludes',
        label: 'Interludes',
        items: [
            {
                name: 'interludecategory',
                label: "Dossiers d'interludes"
            },
            {
                name: 'interlude',
                label: 'Sons Interlude'
            },
        ],
    },
    {
        key: 'events',
        label: 'Événements',
        items: [
            {name: 'event', label: 'Événements'},
            {name: 'fictitiousevent', label: 'Événements fictifs'},
            {name: 'place', label: 'Lieux'},
            {name: 'gift', label: 'Cadeaux'},
            {name: 'contest-admin', label: 'Jeux concours'},
        ],
    },
];

const HANDLED_RESOURCES = new Set([
    'user',
    'song',
    'catalog',
    'song-suggestion',
    'songgenre',
    'songregion',
    'songlang',
    'interludecategory',
    'interlude',
    'introvideo',
    'medley',
    'event',
    'fictitiousevent',
    'place',
    'gift',
    'contest-admin',
    'media',
    'translation',
    ...BOX_RESOURCES,
]);

const resourceIcon = resource =>
    resource && resource.icon
        ? React.createElement(resource.icon)
        : <ViewList />;

const AdminMenu = props => {
    const resources = useSelector(getResources, shallowEqual);
    const getResourceLabel = useGetResourceLabel();
    const location = useLocation();

    const userTotal = useUserTotalCount();

    const prospectTotal =
        useAdminResourceTotalCount('loyaltyadmin');

    const reservationTotal =
        useAdminResourceTotalCount('boxreservation');

    // Menus normaux : contenu non Démo uniquement.
    const songTotal =
        useAdminResourceTotalCount(
            'song',
            'is_demo=false'
        );

    const interludeTotal =
        useAdminResourceTotalCount(
            'interlude',
            'category_is_demo=false'
        );

    const introVideoTotal =
        useAdminResourceTotalCount(
            'introvideo',
            'is_demo=false'
        );

    const medleyTotal =
        useAdminResourceTotalCount(
            'medley',
            'is_demo=false'
        );

    // Menu Démo.
    const demoSongTotal =
        useAdminResourceTotalCount(
            'song',
            'is_demo=true'
        );

    const demoInterludeTotal =
        useAdminResourceTotalCount(
            'interlude',
            'category_is_demo=true'
        );

    const demoIntroVideoTotal =
        useAdminResourceTotalCount(
            'introvideo',
            'is_demo=true'
        );

    const demoMedleyTotal =
        useAdminResourceTotalCount(
            'medley',
            'is_demo=true'
        );

    // song_id=null = toutes les suggestions visibles dans "À traiter".
    const suggestionTotal = useAdminResourceTotalCount(
        'song-suggestion',
        'song_id=null'
    );

    const eventTotal =
        useAdminResourceTotalCount('event');

    const menuTotals = {
        song: songTotal,
        'song-suggestion': suggestionTotal,
        interlude: interludeTotal,
        event: eventTotal,
    };

    const listResources =
        resources.filter(resource => resource.hasList);

    const getResource = name =>
        listResources.find(resource => resource.name === name);

    const isResourcePath = React.useCallback(
        name =>
            location.pathname === `/${name}` ||
            location.pathname.startsWith(`/${name}/`),
        [location.pathname]
    );

    const isDemoPath =
        location.pathname === '/demo' ||
        location.pathname.startsWith('/demo/');

    const isDemoInterludePath =
        location.pathname.startsWith('/demo/interlude');

    const isBoxPath =
        BOX_RESOURCES.some(isResourcePath);

    const [boxOpen, setBoxOpen] =
        React.useState(isBoxPath);

    const [demoOpen, setDemoOpen] =
        React.useState(isDemoPath);

    const [
        demoInterludesOpen,
        setDemoInterludesOpen
    ] = React.useState(isDemoInterludePath);

    const [openGroups, setOpenGroups] = React.useState(() => {
        const result = {};

        GROUPS.forEach(group => {
            result[group.key] = group.items.some(item =>
                isResourcePath(item.name)
            );
        });

        return result;
    });

    React.useEffect(() => {
        if (isBoxPath) {
            setBoxOpen(true);
        }
    }, [isBoxPath]);

    React.useEffect(() => {
        if (isDemoPath) {
            setDemoOpen(true);
        }

        if (isDemoInterludePath) {
            setDemoInterludesOpen(true);
        }
    }, [isDemoPath, isDemoInterludePath]);

    React.useEffect(() => {
        const activeGroup = GROUPS.find(group =>
            group.items.some(item =>
                isResourcePath(item.name)
            )
        );

        if (!activeGroup) {
            return;
        }

        setOpenGroups(current => {
            if (current[activeGroup.key]) {
                return current;
            }

            return {
                ...current,
                [activeGroup.key]: true,
            };
        });
    }, [isResourcePath]);

    const renderLink = (
        name,
        label = null,
        indented = false,
        primaryText = null
    ) => {
        const resource = getResource(name);

        if (!resource) {
            return null;
        }

        const resolvedPrimaryText =
            primaryText !== null
                ? primaryText
                : label || getResourceLabel(name, 2);

        return (
            <MenuItemLink
                key={name}
                to={{
                    pathname: `/${name}`,
                    state: {_scrollToTop: true},
                }}
                primaryText={
                    indented
                        ? resolvedPrimaryText
                        : <span style={{fontWeight: 700}}>
                            {resolvedPrimaryText}
                        </span>
                }
                leftIcon={resourceIcon(resource)}
                style={
                    indented
                        ? {paddingLeft: 32}
                        : undefined
                }
                dense={props.dense}
            />
        );
    };

    const renderDemoLink = (
        path,
        resourceName,
        label,
        total,
        paddingLeft = 32
    ) => {
        const resource = getResource(resourceName);

        if (!resource) {
            return null;
        }

        return (
            <MenuItemLink
                key={path}
                to={{
                    pathname: path,
                    state: {_scrollToTop: true},
                }}
                primaryText={
                    total === undefined
                        ? label
                        : <ResourceCountLabel
                            label={label}
                            total={total}
                        />
                }
                leftIcon={resourceIcon(resource)}
                style={{paddingLeft}}
                dense={props.dense}
            />
        );
    };

    const renderGroup = group => (
        <React.Fragment key={group.key}>
            <ListItem
                button
                onClick={() =>
                    setOpenGroups(current => ({
                        ...current,
                        [group.key]:
                            !current[group.key],
                    }))
                }
            >
                <ListItemIcon>
                    <ViewList />
                </ListItemIcon>

                <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                        style: {fontWeight: 700},
                    }}
                />

                {openGroups[group.key]
                    ? <ExpandLess />
                    : <ExpandMore />}
            </ListItem>

            <Collapse
                in={Boolean(openGroups[group.key])}
                timeout="auto"
                unmountOnExit
            >
                {group.items.map(item => {
                    const total =
                        menuTotals[item.name];

                    return renderLink(
                        item.name,
                        item.label,
                        true,
                        total === undefined
                            ? null
                            : <ResourceCountLabel
                                label={item.label}
                                total={total}
                            />
                    );
                })}
            </Collapse>
        </React.Fragment>
    );

    const userLabel = (
        <span
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <span>Utilisateurs</span>
            <UserCountBadge total={userTotal} />
        </span>
    );

    return (
        <div
            style={{
                height: '100%',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 96,
            }}
        >
            <Menu {...props}>
                {renderLink(
                    'user',
                    'Utilisateurs',
                    false,
                    userLabel
                )}

                <React.Fragment key="box-boukan-menu">
                    <ListItem
                        button
                        onClick={() =>
                            setBoxOpen(open => !open)
                        }
                    >
                        <ListItemIcon>
                            <ViewList />
                        </ListItemIcon>

                        <ListItemText
                            primary="Box Boukan"
                            primaryTypographyProps={{
                                style: {fontWeight: 700},
                            }}
                        />

                        {boxOpen
                            ? <ExpandLess />
                            : <ExpandMore />}
                    </ListItem>

                    <Collapse
                        in={boxOpen}
                        timeout="auto"
                        unmountOnExit
                    >
                        {BOX_RESOURCES.map(name => {
                            const resource =
                                getResource(name);

                            if (!resource) {
                                return null;
                            }

                            return (
                                <MenuItemLink
                                    key={name}
                                    to={{
                                        pathname: `/${name}`,
                                        state: {
                                            _scrollToTop: true,
                                        },
                                    }}
                                    primaryText={
                                        name === 'loyaltyadmin'
                                            ? <ResourceCountLabel
                                                label={BOX_LABELS[name]}
                                                total={prospectTotal}
                                            />
                                            : name === 'boxreservation'
                                            ? <ResourceCountLabel
                                                label={BOX_LABELS[name]}
                                                total={reservationTotal}
                                            />
                                            : BOX_LABELS[name]
                                    }
                                    leftIcon={
                                        resourceIcon(resource)
                                    }
                                    style={{
                                        paddingLeft: 32,
                                    }}
                                    dense={props.dense}
                                />
                            );
                        })}
                    </Collapse>
                </React.Fragment>

                {renderGroup(GROUPS[0])}

                {renderGroup(GROUPS[1])}

                {renderLink(
                    'introvideo',
                    'Vidéos introduction',
                    false,
                    <ResourceCountLabel
                        label="Vidéos introduction"
                        total={introVideoTotal}
                    />
                )}

                {renderLink(
                    'medley',
                    'Medleys',
                    false,
                    <ResourceCountLabel
                        label="Medleys"
                        total={medleyTotal}
                    />
                )}

                {/* =============================
                    MENU DÉMO
                    ============================= */}
                <React.Fragment key="demo-menu">
                    <ListItem
                        button
                        onClick={() =>
                            setDemoOpen(open => !open)
                        }
                    >
                        <ListItemIcon>
                            <ViewList />
                        </ListItemIcon>

                        <ListItemText
                            primary="Démo"
                            primaryTypographyProps={{
                                style: {fontWeight: 700},
                            }}
                        />

                        {demoOpen
                            ? <ExpandLess />
                            : <ExpandMore />}
                    </ListItem>

                    <Collapse
                        in={demoOpen}
                        timeout="auto"
                        unmountOnExit
                    >
                        {renderDemoLink(
                            '/demo/song',
                            'song',
                            'Karaoké Démo',
                            demoSongTotal
                        )}

                        <ListItem
                            button
                            onClick={() =>
                                setDemoInterludesOpen(
                                    open => !open
                                )
                            }
                            style={{paddingLeft: 32}}
                        >
                            <ListItemIcon>
                                <ViewList />
                            </ListItemIcon>

                            <ListItemText
                                primary="Interludes Démo"
                            />

                            {demoInterludesOpen
                                ? <ExpandLess />
                                : <ExpandMore />}
                        </ListItem>

                        <Collapse
                            in={demoInterludesOpen}
                            timeout="auto"
                            unmountOnExit
                        >
                            {renderDemoLink(
                                '/demo/interludecategory',
                                'interludecategory',
                                "Dossiers Démo",
                                undefined,
                                48
                            )}

                            {renderDemoLink(
                                '/demo/interlude',
                                'interlude',
                                'Sons Démo',
                                demoInterludeTotal,
                                48
                            )}
                        </Collapse>

                        {renderDemoLink(
                            '/demo/introvideo',
                            'introvideo',
                            'Vidéos introduction Démo',
                            demoIntroVideoTotal
                        )}

                        {renderDemoLink(
                            '/demo/medley',
                            'medley',
                            'Medleys Démo',
                            demoMedleyTotal
                        )}
                    </Collapse>
                </React.Fragment>

                {renderGroup(GROUPS[2])}

                {renderLink('media')}

                {renderLink('translation')}

                {listResources
                    .filter(
                        resource =>
                            !HANDLED_RESOURCES.has(
                                resource.name
                            )
                    )
                    .map(resource =>
                        renderLink(resource.name)
                    )}

                <div
                    style={{
                        padding: '0 16px 8px',
                        fontSize: 12,
                        fontWeight: 700,
                        opacity: 0.7
                    }}
                >
                    ANALYTIQUE
                </div>

                <MenuItemLink
                    to={{pathname: '/tnr'}}
                    primaryText="TNR"
                />
</Menu>
        </div>
    );
};

export default AdminMenu;
